import Icon from 'assets/icons';
import { User } from 'models/User';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import ApplicationService from 'services/Application';
import { logout } from 'store/reducers/authentication';
import * as Dialog from '@radix-ui/react-dialog';
import { set } from 'lodash';

const authService = new ApplicationService();
type LandingType = "uniqueLink" | "inviteLink" | "programLink";

interface LandingRouteInfo {
    landingType: LandingType | null;
    token: string | null;
    emailid?: string | null;
    name?: string | null;
}

export const useLandingRouteInfo = (): LandingRouteInfo => {
    const location = useLocation();
    const pathSegment = location.pathname.split("/")[1];
    const validLandingTypes: LandingType[] = ["uniqueLink", "inviteLink", "programLink"];
    const landingType = validLandingTypes.includes(pathSegment as LandingType)
        ? (pathSegment as LandingType)
        : null;
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    // const emailid = landingType === "uniqueLink" ? queryParams.get("emailid") : undefined;
    // const name = landingType === "uniqueLink" ? queryParams.get("name") : undefined;

    return { landingType, token };
};

function LandingPage() {
    const { landingType, token } = useLandingRouteInfo();
    // const learnerName = name ? name : "Learner";
    const userState = useSelector((state: RootState) => state.auth.user as User);
    const dispatch = useDispatch();
    const [user, setUser] = useState<User | null>(null);
    const [programInfo, setProgramInfo] = useState<any>({});
    const [isSignedUp, setIsSignedUp] = useState<boolean | null>(null);
    const [sessionCode, setSessionCode] = useState<string>('');
    const [isSessionCodeValid, setIsSessionCodeValid] = useState<boolean | null>(false);
    const [sessionCodeError, setSessionCodeError] = useState<string | null>(null);
    const [sessionCodeMsg, setSessionCodeMsg] = useState<string | null>(null);
    const [shouldShowLogin, setShouldShowLogin] = useState<boolean>(false);
    const [shouldShowSignup, setShouldShowSignup] = useState<boolean>(false);
    const [shouldShowContinue, setShouldShowContinue] = useState<boolean>(false);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [emailid, setEmailid] = useState<string | null>('');
    const [name, setName] = useState<string | null>('');

    const navigate = useNavigate();
    const location = useLocation();

    const handleLogin = () => {
        const queryParams = new URLSearchParams();
        if (token) queryParams.set('token', token);
        if (landingType === "uniqueLink" && emailid) queryParams.set('emailid', emailid);
        navigate(`/login?${queryParams.toString()}`, {
            state: { from: location.pathname + location.search },
        });
    };

    const handleSignup = () => {
        const queryParams = new URLSearchParams();
        if (token) queryParams.set('token', token);
        if (emailid) queryParams.set('emailid', emailid);
        navigate(`/signup?${queryParams.toString()}`, {
            state: { from: location.pathname + location.search },
        });
    };

    const handleContinue = async () => {
        console.log("userState", userState);

        if (landingType === "programLink" && token && userState?.email) {
            const validationResponse: any = await authService.validateOpenLinkUser(token, userState.email);
            console.log('validationResponse', validationResponse);

            if (validationResponse?.success) {
                // setIsDialogOpen(true);
                navigate('/dashboard');
            } else {
                navigate('/error', { state: { email: userState.email, message: validationResponse?.message || 'Validation failed' } });
            }
        } else if (landingType === "uniqueLink" && token && userState?.email && emailid) {
            if (userState.email === emailid) {
                const validationResponse: any = await authService.validateUniqueLinkUser(token, userState.email);
                console.log('validationResponse', validationResponse);

                if (validationResponse?.success) {
                    // setIsDialogOpen(true);
                    navigate('/dashboard');

                } else {
                    navigate('/error', { state: { email: validationResponse?.message || 'Validation failed' } });
                }
            } else {
                dispatch(logout());
                const queryParams = new URLSearchParams();
                if (token) queryParams.set('token', token);
                queryParams.set('emailid', emailid);
                navigate(`/login?${queryParams.toString()}`, {
                    state: { from: location.pathname + location.search },
                });
            }
        } else if (landingType === "inviteLink" && token && userState?.email && isSessionCodeValid) {
            const validationResponse: any = await authService.validateInviteLinkUser(token, userState.email, sessionCode);
            if (validationResponse?.success) {
                // setIsDialogOpen(true);
                navigate('/dashboard');
            } else {
                navigate('/error', { state: { email: userState.email, message: validationResponse?.message || 'Validation failed' } });
            }
        } else {
            navigate('/dashboard');
        }
    };

    useEffect(() => {
        console.log("sessionCode", isSessionCodeValid);

    }, [isSessionCodeValid]);

    useEffect(() => {
        console.log("shouldShowLogin", shouldShowLogin);

    }, [shouldShowLogin]);

    const handleSessionCodeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !sessionCode) {
            setSessionCodeError('Please enter a session code.');
            return;
        }
        try {
            const validationResponse: any = await authService.validateSessionCode(token, sessionCode);
            console.log('validationResponse', validationResponse);

            if (validationResponse?.success) {
                setSessionCodeMsg(validationResponse.message);
                if (isSessionCodeValid) {
                    if (userState?.email) {
                        setIsSignedUp(true);
                        setShouldShowLogin(false);
                        setShouldShowSignup(false);
                        setShouldShowContinue(true);
                    } else {
                        setIsSignedUp(false);
                        setShouldShowLogin(true);
                        setShouldShowSignup(true);
                        setShouldShowContinue(false);
                    }
                }
                // setIsSessionCodeValid(false);
                setSessionCodeError('');
            } else {
                setIsSessionCodeValid(false);
                setSessionCodeError(validationResponse?.data?.message || 'Invalid session code.');
            }
        } catch (error: any) {
            setIsSessionCodeValid(false);
            setSessionCodeError(error.message || 'Session code validation failed.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!landingType || !token) {
                navigate('/error', { state: { message: 'Invalid link or token' } });
                return;
            }
            let data = null;
            try {
                if (landingType === "uniqueLink") {
                    data = await authService.getUniqueLinkProgramInfo(token);
                    if (!data) {
                        navigate('/error', { state: { message: 'Failed to load program information' } });
                        return;
                    }
                    setProgramInfo(data);
                    const userDetailsResponse = await authService.getUserDetailsByUniqueLink(token);
                    console.log('userDetailsResponse', userDetailsResponse);

                    if (userDetailsResponse) {
                        const fetchedEmail = userDetailsResponse.email;
                        const fetchedName = userDetailsResponse.name;

                        setEmailid(fetchedEmail);
                        setName(fetchedName);
                        if (userDetailsResponse?.status === 'REGISTERED') {
                            if (userState && userState.email === fetchedEmail) {
                                setShouldShowLogin(false);
                                setShouldShowContinue(true);
                                return;
                            } else {
                                setShouldShowLogin(true);
                                setShouldShowContinue(false);
                            }
                            if (userState && userState.email !== fetchedEmail) {
                                // dispatch(logout());
                                console.log('userState.email !== emailid');
                                const queryParams = new URLSearchParams();
                                if (token) queryParams.set('token', token);
                                if (fetchedEmail) queryParams.set('emailid', fetchedEmail);
                                navigate(`/login?${queryParams.toString()}`, {
                                    state: { from: location.pathname + location.search },
                                });
                                return;
                            }
                        } else {
                            setShouldShowLogin(false);
                            setShouldShowSignup(true);
                            // setIsSignedUp(false);
                        }
                    } else {
                        // setIsSignedUp(false);
                        console.log('Failed to get user details by unique link');
                        setShouldShowLogin(false);
                        setShouldShowSignup(true);
                    }
                } else if (landingType === "inviteLink") {
                    data = await authService.getInviteLinkProgramInfo(token);
                    if (!data) {
                        navigate('/error', { state: { message: 'Failed to load program information' } });
                        return;
                    }
                    setProgramInfo(data);
                    console.log('data', data);
                    setIsSessionCodeValid(data.isSessionCode ? true : false);
                    if (isSessionCodeValid) {
                        // if (userState?.email) {
                        //     setIsSignedUp(true);
                        //     setShouldShowLogin(false);
                        //     setShouldShowSignup(false);
                        //     setShouldShowContinue(true);
                        // } else {
                        //     setIsSignedUp(false);
                        //     setShouldShowLogin(false);
                        //     setShouldShowSignup(false);
                        //     setShouldShowContinue(false);
                        // }
                        setIsSignedUp(false);
                        setShouldShowLogin(false);
                        setShouldShowSignup(false);
                        setShouldShowContinue(false);
                    }
                    else {
                        if (userState?.email) {
                            setIsSignedUp(true);
                            setShouldShowLogin(false);
                            setShouldShowSignup(false);
                            setShouldShowContinue(true);
                        } else {
                            setIsSignedUp(false);
                            setShouldShowLogin(true);
                            setShouldShowSignup(true);
                            setShouldShowContinue(false);
                        }
                    }

                } else if (landingType === "programLink") {
                    data = await authService.getCompanyAndProgramDetails(token);
                    if (!data) {
                        navigate('/error', { state: { message: 'Failed to load program information' } });
                        return;
                    }
                    setProgramInfo(data);
                    setShouldShowLogin(!userState);
                    setShouldShowContinue(!!userState);
                }
            } catch (error: any) {
                console.error("Failed to fetch program info:", error);
                navigate('/error', { state: { message: error?.response?.data?.message || 'Failed to load program information' } });
            }
        };

        fetchData();
    }, [landingType, userState, isSessionCodeValid]);

    useEffect(() => {
        setUser(userState || null);
    }, [userState]);

    return (
        <div className="relative z-10 bg-[#E9E0FA] bg-opacity-90 rounded-xl 
      mobile:px-[40px] tablet:px-[80px] laptop:px-[100px] px-[150px] 
      pb-[100px] pt-[30px] shadow-lg w-full max-w-[80%]">
            <div className="flex flex-col items-center gap-2 justify-center mb-5">
                <img width={130} src={Icon.secondLogo} alt="Icon Image" />
                <h1 className="mobile:text-xl text-3xl text-[#5F488A] font-bold">Welcome to Viliyo</h1>
            </div>
            <div className="rounded-xl bg-white laptop:px-[80px] tablet:px-[40px] mobile:px-[20px] py-4 px-[130px] space-y-4">
                <div className='flex flex-col justify-center items-center'>
                    <h1 className="text-[#5F488A] text-xl font-bold">Hi {programInfo?.trainee?.trainee_name || name || 'Learner'}</h1>
                    <p className="text-center text-[#5F488A] mt-2">You have been invited to participate in the upcoming programme</p>
                </div>
                <div className="bg-[#E9E0FA] mobile:px-5 px-10 py-5 rounded-lg shadow-md space-y-2">
                    <p className='text-[#5F488A]'><strong>Program Name:</strong> {programInfo?.programBatch?.program?.program_name || "N/A"} </p>
                    <p className='text-[#5F488A]'><strong>No. of Sessions:</strong> {programInfo?.programBatch?.program?.total_sessions || "N/A"}</p>
                    <p className='text-[#5F488A]'><strong>Batch Name:</strong> {programInfo?.programBatch?.batch_name || "N/A"}</p>
                    <p className='text-[#5F488A]'><strong>Program Duration:</strong>{programInfo?.programBatch?.program?.training_days || "N/A"} days</p>
                </div>
                {landingType === "inviteLink" && isSessionCodeValid && (
                    <div className="bg-[#E9E0FA] mobile:px-5 px-10 py-2 rounded-lg shadow-md">
                        <form onSubmit={handleSessionCodeSubmit} className="mobile:flex-col justify-between mobile:gap-2 flex items-center mobile:space-x-0 space-x-4">
                            <label htmlFor="sessionNo" className="text-[#5F488A] font-medium">
                                <strong>Enter Session Code</strong>
                            </label>
                            <input
                                id="sessionNo"
                                type="text"
                                className="mobile:w-full w-2/3 border border-gray-300 rounded px-4 py-2 focus:outline-none"
                                placeholder="e.g., 54566756756"
                                value={sessionCode}
                                onChange={(e) => setSessionCode(e.target.value)}
                            />
                            <button
                                type="submit"
                                className="flex flex-row bg-gradient-to-r from-app-blue to-app-purple text-white font-medium rounded-full px-6 py-2 mobile:px-4 mobile:py-1.5 mobile:text-sm text-base shadow-lg hover:shadow-xl hover:from-app-purple hover:to-app-blue active:scale-95 transition-all duration-200 w-auto min-w-[150px] mobile:min-w-[120px] justify-center"
                            >
                                <span className="flex mx-auto">Submit</span>
                            </button>
                        </form>
                        {sessionCodeError && (
                            <p className="text-red-500 text-sm mt-2">{sessionCodeError}</p>
                        )}
                        {sessionCodeMsg && (
                            <p className="text-red-500 text-sm mt-2">{sessionCodeMsg}</p>
                        )}
                    </div>
                )}
                {/* {(landingType !== "inviteLink" || isSessionCodeValid) && ( */}
                <div className="flex flex-wrap gap-4 tablet:gap-6 laptop:gap-8 desktop:gap-10 largescreen:gap-12 justify-evenly items-center w-full">
                    {shouldShowLogin && (
                        <button
                            onClick={handleLogin}
                            className="flex flex-col items-center justify-center w-full mobile:w-full tablet:w-1/2 laptop:w-1/3 desktop:w-1/4 bg-gradient-to-r shadow text-white from-app-blue to-app-purple py-2 tablet:py-3 rounded-full"
                        >
                            {landingType === "uniqueLink" && emailid ? (
                                <>
                                    <span className="font-medium text-sm tablet:text-base">Login as</span>
                                    <span
                                        className="
              text-xs tablet:text-sm
              truncate max-w-[90%]
              desktop:whitespace-normal desktop:truncate-none desktop:max-w-full
              largescreen:whitespace-normal largescreen:truncate-none largescreen:max-w-full
            "
                                    >
                                        {emailid}
                                    </span>
                                </>
                            ) : (
                                <span className="font-medium text-sm tablet:text-base">Login</span>
                            )}
                        </button>
                    )}

                    {shouldShowSignup && (
                        <button
                            onClick={handleSignup}
                            className="flex flex-col items-center justify-center w-full mobile:w-full tablet:w-1/2 laptop:w-1/3 desktop:w-1/4 bg-gradient-to-r shadow text-white from-app-blue to-app-purple py-2 tablet:py-3 rounded-full"
                        >
                            {landingType === "uniqueLink" && emailid ? (
                                <>
                                    <span className="font-medium text-sm tablet:text-base">Sign Up as</span>
                                    <span
                                        className="text-xs tablet:text-sm truncate max-w-[90%] desktop:whitespace-normal desktop:truncate-none desktop:max-w-full largescreen:whitespace-normal largescreen:truncate-none largescreen:max-w-full"
                                    >
                                        {emailid}
                                    </span>
                                </>
                            ) : (
                                <span className="font-medium text-sm tablet:text-base">Sign Up</span>
                            )}
                        </button>
                    )}

                    {shouldShowContinue && (
                        <button
                            onClick={handleContinue}
                            className="flex flex-col items-center justify-center w-full mobile:w-full tablet:w-1/2 laptop:w-1/3 desktop:w-1/4 bg-gradient-to-r shadow text-white from-app-blue to-app-purple py-2 tablet:py-3 rounded-full"
                        >
                            {landingType === "uniqueLink" && emailid ? (
                                <>
                                    <span className="font-medium text-sm tablet:text-base">Continue as</span>
                                    <span
                                        className="text-xs tablet:text-sm truncate max-w-[90%] desktop:whitespace-normal desktop:truncate-none desktop:max-w-full largescreen:whitespace-normal largescreen:truncate-none largescreen:max-w-full"
                                    >
                                        {emailid}
                                    </span>
                                </>
                            ) : (
                                <span className="font-medium text-sm tablet:text-base">Continue</span>
                            )}
                        </button>
                    )}
                </div>

                {/* )} */}
            </div>
            {/* <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20" />
                    <Dialog.Content
                        className="fixed z-30 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
              w-[90vw] max-w-sm rounded-xl bg-white p-6 shadow-xl focus:outline-none"
                    >
                        <Dialog.Title className="text-xl font-bold text-center text-[#5F488A]">
                            Continue with the app
                        </Dialog.Title>
                        <Dialog.Close asChild>
                            <button
                                className="mt-6 w-full px-6 py-2 rounded-full bg-gradient-to-r
                  from-app-blue to-app-purple text-white font-medium shadow"
                                onClick={() => navigate('/dashboard')}
                            >
                                Continue

                            </button>
                        </Dialog.Close>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root> */}
        </div>
    );
}

export default LandingPage;